import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import {
  Monitor,
  Plus,
  Trash2,
  Activity,
  Zap,
  X,
  ShieldCheck,
  Cpu,
  Copy,
  AlertTriangle,
  Download,
  ArrowUpCircle,
  RefreshCw,
  Search,
  RotateCcw,
  ArrowUpDown,
} from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  status: string;
  version?: string;
  os_version?: string;
  ip_address?: string;
  last_heartbeat?: string;
  created_at: string;
  update_available?: boolean;
  latest_available_version?: string;
}

export default function Agents() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('agents');
  const { showSuccess, showConfirm, showError } = useDialog();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [installDir, setInstallDir] = useState('');
  const [serviceSuffix, setServiceSuffix] = useState('');
  const [healthPort, setHealthPort] = useState(18082);
  const [creating, setCreating] = useState(false);
  const [newAgent, setNewAgent] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'last_heartbeat'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await api.get('/agents');
      setAgents(response.data || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await api.post('/agents', {
        name: agentName,
        instance_name: instanceName,
        install_dir: installDir || `C:\\Program Files\\pgkb-agent\\${instanceName}`,
        service_name: `pgkb-${serviceSuffix || instanceName}`,
        health_port: healthPort,
      });
      setNewAgent(response.data);
      setShowCreateModal(false);
      setAgentName('');
      setInstanceName('');
      setInstallDir('');
      setServiceSuffix('');
      setHealthPort(18082);
      fetchAgents();
    } catch (error: any) {
      const errorMessage =
        error?.userMessage || error?.appError?.userMessage || t('wizard.createError');
      await showError(t('wizard.createErrorTitle'), errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const filteredAndSortedAgents = agents
    .filter((agent) => {
      const matchesSearch = agent.name.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter
        ? agent.status.toLowerCase() === statusFilter.toLowerCase()
        : true;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      } else if (sortBy === 'last_heartbeat') {
        const aDate = a.last_heartbeat ? new Date(a.last_heartbeat).getTime() : 0;
        const bDate = b.last_heartbeat ? new Date(b.last_heartbeat).getTime() : 0;
        comparison = aDate - bDate;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSortBy('name');
    setSortOrder('asc');
  };

  const handleDelete = async (agent: Agent) => {
    const confirmed = await showConfirm(
      t('delete.confirmTitle'),
      t('delete.confirmMessage', { name: agent.name }),
      'warning'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/agents?id=${agent.id}`);
      // Show success dialog and wait for it
      await showSuccess(t('delete.successTitle'), t('delete.successMessage', { name: agent.name }));
      // Refresh after dialog closes
      void fetchAgents();
    } catch (error: any) {
      if (error.response?.status === 409) {
        await showError(t('delete.errorHasConfigsTitle'), t('delete.errorHasConfigsMessage'));
      } else {
        await showError(t('delete.errorTitle'), t('delete.errorMessage'));
      }
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="secondary"
              onClick={fetchAgents}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{' '}
              {t('refresh')}
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              size="lg"
              className="w-full sm:w-auto whitespace-nowrap shadow-brand-200"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('addNewAgent')}
            </Button>
          </div>
        </div>

        <Card className="p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:flex">
            <div className="relative min-w-0 xl:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
              <input
                type="text"
                aria-label={t('filters.searchAgents')}
                placeholder={t('filters.searchAgents')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full min-h-11 pl-10 pr-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm focus:ring-2 focus:ring-brand-500 transition-all"
              />
            </div>
            <select
              aria-label={t('filters.allStatuses')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="min-h-11 w-full px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500 xl:w-auto"
            >
              <option value="">{t('filters.allStatuses')}</option>
              <option value="online">{t('status.online')}</option>
              <option value="offline">{t('status.offline')}</option>
              <option value="revoked">{t('status.revoked')}</option>
            </select>
            <select
              aria-label={t('sort.label')}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'status' | 'last_heartbeat')}
              className="min-h-11 w-full px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500 xl:w-auto"
            >
              <option value="name">{t('sort.byName')}</option>
              <option value="status">{t('sort.byStatus')}</option>
              <option value="last_heartbeat">{t('sort.byLastHeartbeat')}</option>
            </select>
            <div className="flex flex-col gap-4 sm:flex-row md:justify-end">
              <Button
                variant="secondary"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="min-h-11 md:w-auto"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />{' '}
                {sortOrder === 'asc' ? t('sort.ascending') : t('sort.descending')}
              </Button>
              <Button variant="secondary" onClick={resetFilters} className="min-h-11 md:w-auto">
                <RotateCcw className="w-4 h-4 mr-2" /> {t('sort.reset')}
              </Button>
            </div>
          </div>
        </Card>

        {loadError && agents.length > 0 && (
          <div role="alert">
            <Card className="flex flex-col gap-3 border-state-danger-light dark:border-state-danger-dark sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark">
                {t('list.loadFailed')}
              </p>
              <Button variant="secondary" onClick={fetchAgents} disabled={loading}>
                {t('list.retry')}
              </Button>
            </Card>
          </div>
        )}

        {loading && agents.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-black uppercase tracking-widest text-[10px]">
              {t('list.loading')}
            </p>
          </div>
        ) : loadError && agents.length === 0 ? (
          <div role="alert">
            <Card className="flex flex-col items-center gap-4 py-16 text-center">
              <p className="max-w-lg text-sm font-medium text-state-danger-light dark:text-state-danger-dark">
                {t('list.loadFailed')}
              </p>
              <Button variant="secondary" onClick={fetchAgents} disabled={loading}>
                {t('list.retry')}
              </Button>
            </Card>
          </div>
        ) : agents.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300 dark:text-slate-600">
              <Monitor size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('list.noAgentsConnected.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {t('list.noAgentsConnected.description')}
            </p>
            <Button variant="outline" className="mt-8" onClick={() => setShowCreateModal(true)}>
              {t('list.noAgentsConnected.button')}
            </Button>
          </Card>
        ) : filteredAndSortedAgents.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <div className="w-20 h-20 bg-surface-2-light dark:bg-surface-2-dark rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300 dark:text-slate-600">
              <Search size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('list.noAgentsMatchFilters.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2 max-w-sm mx-auto font-medium text-sm">
              {t('list.noAgentsMatchFilters.description')}
            </p>
            <Button variant="outline" className="mt-8" onClick={resetFilters}>
              {t('list.noAgentsMatchFilters.button')}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredAndSortedAgents.map((agent) => (
              <Card
                key={agent.id}
                className="p-6 group relative overflow-hidden transition-all hover:border-brand-200"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark group-hover:bg-brand-600 group-hover:text-white transition-all duration-500 shadow-sm">
                    <Cpu size={24} />
                  </div>
                  <Badge variant={toneToBadgeVariant[getStatusTone(agent.status)]}>
                    {t(`status.${agent.status.toLowerCase()}`, { defaultValue: agent.status })}
                  </Badge>
                </div>

                <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight mb-1 truncate">
                  {agent.name}
                </h3>
                <div className="flex items-center text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-6">
                  <Activity
                    size={12}
                    className="mr-1.5 text-state-ok-light dark:text-state-ok-dark"
                  />
                  {t('labels.lastSeen')}{' '}
                  {agent.last_heartbeat
                    ? new Intl.DateTimeFormat(i18n.language, {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      }).format(new Date(agent.last_heartbeat))
                    : t('labels.never')}
                </div>

                <div className="space-y-3 mb-8">
                  <InfoItem
                    icon={<Zap size={14} />}
                    label={t('labels.version')}
                    value={agent.version || t('labels.na')}
                  />
                  <InfoItem
                    icon={<Monitor size={14} />}
                    label={t('labels.os')}
                    value={agent.os_version || t('labels.windows')}
                  />
                  <InfoItem
                    icon={<ShieldCheck size={14} />}
                    label={t('labels.ip')}
                    value={agent.ip_address || '0.0.0.0'}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="min-h-11 flex-1"
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  >
                    {t('actions.manage')}
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t('actions.deleteAgent', { name: agent.name })}
                    className="text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                    title={t('actions.delete')}
                    onClick={() => handleDelete(agent)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>

                {agent.update_available && (
                  <div className="absolute top-0 right-0 p-2">
                    <div
                      className="bg-state-warn-light dark:bg-state-warn-dark text-white p-1.5 rounded-bl-xl shadow-lg animate-pulse"
                      title={t('labels.updateAvailable')}
                    >
                      <ArrowUpCircle size={16} />
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {newAgent && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-500">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 text-center space-y-6 bg-surface-card-light dark:bg-surface-card-dark border-none shadow-2xl">
            <div className="w-20 h-20 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto text-state-ok-light dark:text-state-ok-dark">
              <ShieldCheck size={40} />
            </div>
            <div>
              <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                {t('wizard.securityTokenGenerated')}
              </h2>
              <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-2">
                {t('wizard.securityTokenDesc')}
              </p>
            </div>
            <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border-2 border-dashed border-surface-border-light dark:border-surface-border-dark group relative">
              <code className="text-xl font-black text-brand-600 dark:text-brand-400 break-all">
                {newAgent.token}
              </code>
              <button
                onClick={async () => {
                  navigator.clipboard.writeText(newAgent.token);
                  await showSuccess(t('wizard.tokenCopied'), t('wizard.tokenCopiedDesc'));
                }}
                className="absolute -top-3 -right-3 bg-surface-card-light dark:bg-surface-card-dark shadow-md p-2 rounded-xl text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400 transition-colors border border-surface-border-light dark:border-surface-border-dark"
              >
                <Copy size={16} />
              </button>
            </div>
            {newAgent.installation && (
              <div className="text-sm space-y-1 break-all text-left p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl">
                <p>Instância: {newAgent.installation.instance_name}</p>
                <p>Pasta: {newAgent.installation.install_dir}</p>
                <p>Serviço: {newAgent.installation.service_name}</p>
                <p>Porta local: {newAgent.installation.health_port}</p>
              </div>
            )}
            <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-2xl flex items-start space-x-3 text-left">
              <AlertTriangle
                className="text-state-warn-light dark:text-state-warn-dark shrink-0"
                size={20}
              />
              <p className="text-xs text-state-warn-light dark:text-state-warn-dark font-bold leading-relaxed uppercase">
                {t('wizard.tokenWarning')}
              </p>
            </div>
            {newAgent.download_url && (
              <div className="space-y-2">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => {
                    window.location.href = newAgent.download_url;
                  }}
                >
                  <Download size={18} className="mr-2" />
                  {t('wizard.downloadWindowsInstaller')}
                </Button>
                <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                  {t('wizard.downloadWindowsInstallerDesc')}
                </p>
              </div>
            )}
            <Button
              size="lg"
              variant="secondary"
              className="w-full"
              onClick={() => setNewAgent(null)}
            >
              {t('wizard.iveSavedToken')}
            </Button>
          </Card>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-elevated">
            <div className="p-8 space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('wizard.pairNewAgent')}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  aria-label={t('wizard.close')}
                  className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-colors"
                >
                  <X size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
                </button>
              </div>
              <form onSubmit={handleCreateAgent} className="space-y-6">
                <Input
                  id="agent-display-name"
                  label={t('wizard.friendlyName')}
                  placeholder={t('wizard.friendlyNamePlaceholder')}
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  required
                />
                <Input
                  id="agent-instance-name"
                  label={t('wizard.instanceName')}
                  placeholder={t('wizard.instanceNamePlaceholder')}
                  value={instanceName}
                  maxLength={15}
                  pattern="[A-Za-z0-9_\-]{1,15}"
                  required
                  onChange={(event) => setInstanceName(event.target.value)}
                />
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('wizard.instanceNameHint')}
                </p>
                <details className="space-y-4">
                  <summary className="cursor-pointer font-medium">
                    {t('wizard.advancedOptions')}
                  </summary>
                  <Input
                    id="agent-install-dir"
                    label={t('wizard.installDirectory')}
                    value={installDir || `C:\\Program Files\\pgkb-agent\\${instanceName}`}
                    maxLength={240}
                    onChange={(event) => setInstallDir(event.target.value)}
                  />
                  <Input
                    id="agent-service-suffix"
                    label={t('wizard.serviceName')}
                    value={serviceSuffix || instanceName}
                    maxLength={15}
                    pattern="[A-Za-z0-9_\-]{1,15}"
                    required
                    onChange={(event) => setServiceSuffix(event.target.value)}
                  />
                  <Input
                    id="agent-health-port"
                    label={t('wizard.healthPort')}
                    type="number"
                    min={1024}
                    max={65535}
                    required
                    value={healthPort}
                    onChange={(event) => setHealthPort(Number(event.target.value))}
                  />
                  <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                    {t('wizard.healthPortHint')}
                  </p>
                </details>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t('wizard.cancel')}
                  </Button>
                  <Button className="flex-1" type="submit" isLoading={creating}>
                    {t('wizard.generateToken')}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-start justify-between gap-3 text-xs">
      <div className="flex shrink-0 items-center text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest">
        <span className="mr-2 opacity-50">{icon}</span>
        {label}
      </div>
      <div className="min-w-0 break-words text-right font-black text-slate-700 dark:text-slate-300">
        {value}
      </div>
    </div>
  );
}
